/*
===================================
|          react-map-gl           |
===================================
*/
import React, { useEffect } from 'react';
import Map, { NavigationControl, FullscreenControl, useMap, Popup, LayerProps, Source, Layer, ScaleControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import "./InitMap.css";
import mapboxgl from 'mapbox-gl';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/reducers/root';
import LoadMarkers from "../MapMarker/MapMarkers";
import PopUpInfo from "../PopUpInfo/PopUpInfo";
import PopUpList from "../PopUpList/PopUpList";
// import MapLegend from "../MapLegend/MapLegend";
import AmpLoadingSpinner from '../../../common/AmpLoadingSpinner/AmpLoadingSpinner';
import { getRollFromCentroid } from '../../../../utils/getRollFromCentroid';
import MapLegendButton from '../BottomButtons/MapLegend';
/* eslint @typescript-eslint/no-var-requires: "off" */
import CustomOverlay from '../CustomControls/CustomControls';
import DarkModeButton from '../BottomButtons/DarkMode';
import SattliteButton from '../TopButtons/Sattlite';
import OpenGoogleMapsButton from '../TopButtons/GoogleMap';
import { setToastNotification } from '../../../../redux/actions/toastNotification/toastNotification';
import { CityResponse } from "../../../../redux/reducers/getNBHDReducer/getCityReducer";
import { searchCityCoords } from "../../../../redux/actions/nbhd/search";
import * as drawCircle from '@turf/circle';
import "./IconOverrides.css"

export interface MapProp {
  setSubPropCoord: CallableFunction;
  setSubPropMun: CallableFunction;
}

export default function MapboxGLMap(props: MapProp) {
  const { mainMap } = useMap();

  const SnapshotsList = useSelector((state: RootState) => state.SnapshotsListReducer.SnapshotsList);

  const currentPropertyDetails = useSelector((state: RootState) => state.curPropertyDetailsReducer.currentPropertyDetails);

  const propertyDetails: Record<string, unknown> = currentPropertyDetails["propertyDetails"];

  const snapshots: Record<string, unknown> = SnapshotsList["poiListDetail"];

  const snaps: Array<Record<string, unknown>> | boolean = snapshots ? snapshots["snapshots"] : false;

  const CityCoords = useSelector((state: RootState) => state.NBHDReducer.CityCoords as CityResponse);

  const [currentSnapshotRoll, setCurrentSnapRoll] = useState("");

  const [subjectNoCoord, setSubjectNoCoords] = useState(false);

  const dispatch = useDispatch();
  const [state] = useState({
    lng: -74.3863868,
    lat: 45.1947072,
    zoom: 18
  });

  const setSubPropCoord = props.setSubPropCoord;
  const setSubPropMun = props.setSubPropMun;

  const getMyPropertyCords = () => {

    let spatialCentroid;
    try {
      spatialCentroid = propertyDetails["spatial"]["features"][0]["properties"]["centroid"]
    } catch (error) {
      dispatch(setToastNotification("No Spatial Data available for subject property, Property will be shown at map default", "error"));
      spatialCentroid = {
        lng: state.lng,
        lat: state.lat
      }
    }
    return spatialCentroid;

  }
  const [popupInfo, setPopupInfo] = useState<Record<string, unknown> | null>(null);
  const [popupList, setPopupList] = useState<Record<string, unknown> | null>(null);

  const handleAddClick = (e) => {
    if (e) {
      mainMap?.jumpTo({ center: [e.lngLat.lng, e.lngLat.lat] });
      setPopupInfo(e);
      setPopupList(null);
      getRollFromCentroid(e.lngLat.lat, e.lngLat.lng).then(roll => {
        if (roll.length > 19) {
          setPopupInfo(null);
          setPopupList(e);
        } else {
          setCurrentSnapRoll(roll);
          setPopupInfo(e);
        }

      })
    }
  };

  const flyToMyProp = (e) => {
    if (e) {
      const cords = getMyPropertyCords();
      if (cords) {
        setSubPropCoord([cords["lng"], cords["lat"]]);
        setSubPropMun(propertyDetails["propertyData"]["municipalityDescription"]);
        mainMap?.jumpTo({ center: [cords["lng"], cords["lat"]] });
        if (!popupInfo) {
          e['lngLat'] = { 'lng': cords["lng"], 'lat': cords["lat"] }
          setCurrentSnapRoll(propertyDetails["rollNumber"]);
          setPopupInfo(e);
        }
      } else {
        // if no coords for property, get city coords
        const missingCoords:Array<string> = [];
        const cityFullName =  propertyDetails["propertyData"]["municipalityDescription"];
        if (cityFullName.includes('CITY OF')){
          missingCoords.push(cityFullName.replace('CITY OF ',''));
        } else if (cityFullName.includes('NORTH ')){
            missingCoords.push(cityFullName);
        } else if (cityFullName.includes('SOUTH ')){
            missingCoords.push(cityFullName);
        } else if (cityFullName.includes('st. ')){
            missingCoords.push(cityFullName);
        } else {
            missingCoords.push(cityFullName.substring(0, cityFullName.indexOf(' ')));
        }
        if (missingCoords.length > 0) {
            dispatch(searchCityCoords({cities:[...new Set(missingCoords)]}))
            setSubjectNoCoords(true)
        } else {
            setSubjectNoCoords(false)
        }
      }
      
    }
  }

  useEffect(() =>{
    // after getting coords of cities:
    if (CityCoords["city_coords"] && subjectNoCoord) {
      mainMap?.jumpTo({ center: [CityCoords["city_coords"][0]["lng"], CityCoords["city_coords"][0]["lat"]] });
      mainMap?.setZoom(15);
      dispatch(setToastNotification("No coordinates for given property, locating nearest city", "warning"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [CityCoords, subjectNoCoord]);

  const centerChanged = (e) => {
    if (e && e.roll) {
      setCurrentSnapRoll(e.roll);
      e['lngLat'] = { 'lng': e["viewState"]["longitude"], 'lat': e["viewState"]["latitude"] }
      setPopupInfo(e);
    }
  }
  const parcelLayerStyle: LayerProps = {
    id: 'parcels',
    type: 'raster',
    'source-layer': 'parcels',
    source: 'parcels-source',
    "paint": {
      "raster-opacity": 0.5
    }
  };

  const radius = 100;
  const center = [0, 0];
  const options = {
          steps: Number(64),
          units: String("kilometers"),
        };
  const circle = drawCircle.default(center, radius, options);
  const circleOutline: LayerProps = {
    id: "location-radius-outline",
    type: "line",
    paint: {
      "line-color": "#0094ff",
      "line-width": 3,
    },
  };

  const circleFilled: LayerProps = {
    id: "location-radius",
    type: "fill",
    paint: {
      "fill-color": "#8CCFFF",
      "fill-opacity": 0.5,
    },
  };

  const tileUrl = `${process.env.REACT_APP_AMP_API}/mapbox/get-parcels`;

  mapboxgl.accessToken = process.env.REACT_APP_MB_TOKEN as string
  // @ts-ignore
  // eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
  mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

  if (currentPropertyDetails["loading"]) {
    return <AmpLoadingSpinner loading={true}></AmpLoadingSpinner>
  }

  return (<>
    <Map
      id="mainMap"
      onMoveEnd={centerChanged}
      onLoad={flyToMyProp}
      initialViewState={{
        longitude: state.lng,
        latitude: state.lat,
        zoom: state.zoom
      }}
      style={{ width: 798, height: 795 }}
      mapStyle="mapbox://styles/mapbox/streets-v11"
      doubleClickZoom={false}
      transformRequest={(url: string, resourceType: mapboxgl.ResourceType) => {
        if (url.includes(tileUrl)) {
          return {
            url: url,
            headers: {
              'Authorization': window.localStorage.getItem("amptoken"),
              'Accept': "image/png"
            }
          }
        } else {
          return {
            url: url,
          }
        }
      }}

      onDblClick={handleAddClick}
    >

      <LoadMarkers snapshots={snaps} />

      {popupInfo && (
        <Popup
          anchor="left"
          longitude={popupInfo.lngLat.lng}
          latitude={popupInfo.lngLat.lat}
          onClose={() => { setPopupInfo(null); setCurrentSnapRoll("") }}
          className='popupcard'
        ><PopUpInfo coords={[popupInfo.lngLat.lat, popupInfo.lngLat.lng]} snapRoll={currentSnapshotRoll} snapList={snaps} /></Popup>)}
      {popupList && (
        <Popup
          anchor="left"
          longitude={popupList.lngLat.lng}
          latitude={popupList.lngLat.lat}
          onClose={() => { setPopupList(null); setCurrentSnapRoll("") }}
          className='popuplist'
        ><PopUpList coords={[popupList.lngLat.lat, popupList.lngLat.lng]} snaps={snaps} /></Popup>)}
      <Source id="my-data" type='raster' tiles={[`${tileUrl}/{y}/{x}/{z}`]} tileSize={512}>
        <Layer {...parcelLayerStyle} />
      </Source>
      <Source id="CirlceData" type='geojson' data={circle}> 
        <Layer {...circleOutline} />
        <Layer {...circleFilled} />
      </Source>

      <CustomOverlay position='top-right'>
        <>
          <NavigationControl />
          <FullscreenControl/>
          
          <OpenGoogleMapsButton/>
          <SattliteButton/>
          {/* <FullScreenButton/> */}
        </>
      </CustomOverlay>

      <CustomOverlay position='bottom-right'>
        <>
          <MapLegendButton />
          <DarkModeButton />
        </>
      </CustomOverlay>

      <CustomOverlay position='bottom-left'>
        <>
          <ScaleControl/>
        </>
      </CustomOverlay>

    </Map>

  </>)

}


/*
===================================
|         React MapboxGL          |
|      Component with Hooks       |
===================================
*/
// import React, { useEffect, useRef, useState } from "react";
// import mapboxgl from "mapbox-gl";
// import "mapbox-gl/dist/mapbox-gl.css";
// /* eslint @typescript-eslint/no-var-requires: "off" */
// const styles = {
//   width: "800px",
//   height: "700px"
// };
// const MapboxGLMap = () => {
//   const [map, setMap] = useState(null);
//   const mapContainer = useRef(null);
//   const [state, setState] = useState({
//     lng: -74.3863868,
//     lat: 45.1947072,
//     zoom: 17
//   });
//   useEffect(() => {
//     // @ts-ignore
//     // eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
//     mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;
//     mapboxgl.accessToken = 
//     const initializeMap = ({ setMap, mapContainer }) => {
//       const map = new mapboxgl.Map({
//         container: mapContainer.current,
//         style: "mapbox://styles/mapbox/streets-v11",
//         center: [state.lng,  state.lat],
//         zoom: state.zoom
//        });
//       map.on("load", () => {
//         setMap(map);
//         map.resize();
//       });
//     };
//     if (!map) initializeMap({ setMap, mapContainer });
//   }, [map]);
//   return <div ref={el => (mapContainer.current = el)} style={styles} ></div>;
// };
// export default MapboxGLMap;